import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, RefreshCw, Search, FileText, X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "nandaSA.adminToken";

const STATUSES = [
 ["new", "🆕 Nouvelle"], ["to-qualify", "À qualifier"], ["info-required", "Infos requises"],
 ["quote-in-progress", "Devis en prépa"], ["quote-sent", "Devis envoyé"],
 ["follow-up", "Relance"], ["negotiating", "En négociation"],
 ["accepted", "Acceptée"], ["refused", "Refusée"],
 ["no-follow-up", "Sans suite"], ["archived", "Archivée"],
];

const CAT_LABELS = {
 "refrigeration-industrielle": "Réfrigération industrielle",
 "chambre-froide": "Chambre froide",
 "climatisation": "Climatisation",
 "energies-renouvelables": "Énergies renouvelables",
 "produits-consommables": "Produits & consommables",
 "matieres-premieres": "Matières premières",
 "filiere-cajou": "Filière cajou",
 "conseil": "Conseil",
 "autre": "Autre",
};

export default function AdminQuoteRequests() {
 const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
 const [inputToken, setInputToken] = useState("");
 const [authError, setAuthError] = useState("");
 const [list, setList] = useState(null);
 const [loading, setLoading] = useState(false);
 const [q, setQ] = useState("");
 const [filterCat, setFilterCat] = useState("");
 const [filterStatus, setFilterStatus] = useState("");
 const [selected, setSelected] = useState(null);
 const [note, setNote] = useState("");

 useEffect(() => { document.title = "NANDA SÁ · Admin · Devis"; }, []);

 const fetchList = useCallback(async (tok) => {
 setLoading(true);
 try {
 const url = new URL(`${API}/api/quote-requests`);
 if (filterCat) url.searchParams.set("category", filterCat);
 if (filterStatus) url.searchParams.set("status", filterStatus);
 if (q) url.searchParams.set("q", q);
 const res = await fetch(url, { headers: { "X-Admin-Token": tok } });
 if (res.status === 401) { localStorage.removeItem(STORAGE_KEY); setToken(""); setAuthError("Token invalide."); return; }
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 setList(await res.json);
 } catch { setAuthError("Erreur réseau."); }
 finally { setLoading(false); }
 }, [filterCat, filterStatus, q]);

 useEffect(() => { if (token) fetchList(token); }, [token, fetchList]);

 const onLogin = async (e) => {
 e.preventDefault; setAuthError("");
 const res = await fetch(`${API}/api/admin/verify`, { method: "POST", headers: { "X-Admin-Token": inputToken } });
 if (!res.ok) { setAuthError("Token invalide."); return; }
 localStorage.setItem(STORAGE_KEY, inputToken); setToken(inputToken); setInputToken("");
 };

 const logout = () => { localStorage.removeItem(STORAGE_KEY); setToken(""); setList(null); setSelected(null); };

 const updateStatus = async (ref, newStatus) => {
 await fetch(`${API}/api/quote-requests/${ref}`, {
 method: "PATCH",
 headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
 body: JSON.stringify({ status: newStatus }),
 });
 if (selected?.reference === ref) {
 const detail = await fetch(`${API}/api/quote-requests/${ref}`, { headers: { "X-Admin-Token": token } });
 if (detail.ok) setSelected(await detail.json);
 }
 fetchList(token);
 };

 const addNote = async () => {
 if (!note.trim || !selected) return;
 await fetch(`${API}/api/quote-requests/${selected.reference}`, {
 method: "PATCH",
 headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
 body: JSON.stringify({ note: note.trim }),
 });
 setNote("");
 const detail = await fetch(`${API}/api/quote-requests/${selected.reference}`, { headers: { "X-Admin-Token": token } });
 if (detail.ok) setSelected(await detail.json);
 };

 const downloadAttachment = async (ref, att) => {
 const res = await fetch(`${API}/api/quote-requests/${ref}/attachments/${att.id}`, { headers: { "X-Admin-Token": token } });
 if (!res.ok) return alert("Erreur téléchargement.");
 const blob = await res.blob;
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url; a.download = att.original_name; a.click();
 URL.revokeObjectURL(url);
 };

 const deleteRequest = async (ref) => {
 if (!confirm(`Supprimer la demande ${ref} et ses pièces jointes ?`)) return;
 await fetch(`${API}/api/quote-requests/${ref}`, { method: "DELETE", headers: { "X-Admin-Token": token } });
 setSelected(null); fetchList(token);
 };

 const exportCsv = () => {
 if (!list || list.length === 0) return;
 const headers = ["reference", "created_at", "status", "category", "first_name", "last_name", "company", "country", "email", "phone", "budget", "deadline", "attachments_count", "locale"];
 const rows = list.map((d) => headers.map((h) => {
 if (h === "attachments_count") return (d.attachments || []).length;
 const v = d[h] ?? ""; return `"${String(v).replace(/"/g, '""')}"`;
 }).join(","));
 const csv = "\ufeff" + headers.join(",") + "\n" + rows.join("\n");
 const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
 const a = document.createElement("a");
 a.href = URL.createObjectURL(blob); a.download = `nanda-sa-devis-${new Date().toISOString().slice(0,10)}.csv`; a.click();
 };

 // LOGIN
 if (!token) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-nanda-bgAlt" data-testid="admin-quote-login">
 <form onSubmit={onLogin} className="bg-white border border-nanda-line p-8 md:p-10 w-full max-w-md">
 <h1 className="font-serif text-3xl mb-2 text-nanda-ink">Admin · Devis B2B</h1>
 <p className="text-sm text-nanda-muted mb-6">Consultation des demandes de devis et pièces jointes.</p>
 <input type="password" placeholder="Token administrateur" autoFocus value={inputToken} onChange={(e) => setInputToken(e.target.value)} className="w-full border border-nanda-line px-4 py-3 mb-3 focus:outline-none focus:border-nanda-ink" data-testid="admin-quote-token" />
 {authError && <div className="text-sm text-nanda-terracotta mb-3">{authError}</div>}
 <button type="submit" className="w-full bg-nanda-ink text-white px-6 py-3 label-overline hover:bg-nanda-terracotta transition-colors" data-testid="admin-quote-login-btn">Se connecter</button>
 <Link to="/" className="mt-4 block text-center text-xs text-nanda-muted hover:text-nanda-ink label-overline">← Retour à l'accueil</Link>
 </form>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-nanda-bgAlt" data-testid="admin-quote-page">
 <header className="bg-white border-b border-nanda-line">
 <div className="mx-auto max-w-[1600px] px-6 py-5 flex flex-wrap items-center gap-4 justify-between">
 <div>
 <h1 className="font-serif text-2xl text-nanda-ink">Devis B2B</h1>
 <p className="text-xs text-nanda-muted">{list ? `${list.length} demande(s)` : ""}</p>
 </div>
 <div className="flex flex-wrap items-center gap-2">
 <Link to="/admin/leads" className="text-xs label-overline text-nanda-ink border border-nanda-line px-3 py-2 hover:bg-nanda-bgAlt">Voir prospects</Link>
 <button onClick={() => fetchList(token)} className="text-xs label-overline text-nanda-ink border border-nanda-line px-3 py-2 hover:bg-nanda-bgAlt inline-flex items-center gap-2" data-testid="admin-quote-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Rafraîchir</button>
 <button onClick={exportCsv} className="text-xs label-overline text-nanda-ink border border-nanda-line px-3 py-2 hover:bg-nanda-bgAlt inline-flex items-center gap-2" data-testid="admin-quote-export"><Download size={14} /> CSV</button>
 <button onClick={logout} className="text-xs label-overline text-nanda-terracotta border border-nanda-terracotta px-3 py-2 hover:bg-nanda-terracotta hover:text-white inline-flex items-center gap-2"><LogOut size={14} /> Déconnexion</button>
 </div>
 </div>
 </header>

 <div className="mx-auto max-w-[1600px] px-6 py-6">
 <div className="bg-white border border-nanda-line p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
 <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nanda-muted" /><input placeholder="Rechercher (nom, e-mail, entreprise, réf)…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full border border-nanda-line pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-nanda-ink" data-testid="admin-quote-search" /></div>
 <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="border border-nanda-line px-3 py-2 text-sm" data-testid="admin-quote-filter-cat">
 <option value="">Toutes catégories</option>
 {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
 </select>
 <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-nanda-line px-3 py-2 text-sm" data-testid="admin-quote-filter-status">
 <option value="">Tous statuts</option>
 {STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
 </select>
 <button onClick={() => fetchList(token)} className="bg-nanda-ink text-white px-4 py-2 text-xs label-overline hover:bg-nanda-terracotta transition-colors">Filtrer</button>
 </div>

 {list?.length === 0 && (
 <div className="text-center py-16 text-nanda-muted italic">Aucune demande pour le moment.</div>
 )}

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 {(list || []).map((d) => (
 <button key={d.reference} onClick={() => setSelected(d)} className="text-left bg-white border border-nanda-line p-5 hover:border-nanda-ink transition-colors" data-testid={`admin-quote-row-${d.reference}`}>
 <div className="flex items-center justify-between gap-2 mb-2">
 <div className="font-serif text-nanda-ink text-lg tabular-nums">{d.reference}</div>
 <span className="text-[10px] uppercase tracking-widest bg-nanda-bgAlt px-2 py-1 border border-nanda-line">{d.status}</span>
 </div>
 <div className="text-sm text-nanda-ink font-medium">{d.first_name} {d.last_name} · {d.company}</div>
 <div className="text-xs text-nanda-muted mt-1">{d.email} · {d.phone} · {d.country}</div>
 <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
 <span className="bg-nanda-ink text-white px-2 py-1">{CAT_LABELS[d.category] || d.category}</span>
 {d.budget && <span className="border border-nanda-line px-2 py-1">{d.budget}</span>}
 {(d.attachments || []).length > 0 && <span className="text-nanda-terracotta">📎 {(d.attachments || []).length}</span>}
 <span className="ml-auto text-nanda-muted">{new Date(d.created_at).toLocaleString("fr-FR")}</span>
 </div>
 </button>
 ))}
 </div>
 </div>

 {/* MODAL DETAIL */}
 {selected && (
 <div className="fixed inset-0 z-50 bg-nanda-ink/70 backdrop-blur-sm flex items-start justify-center p-4 md:p-10 overflow-y-auto" onClick={() => setSelected(null)} data-testid="admin-quote-modal">
 <div className="bg-white max-w-4xl w-full my-auto" onClick={(e) => e.stopPropagation}>
 <div className="border-b border-nanda-line p-5 flex items-start justify-between gap-3">
 <div>
 <div className="label-overline text-nanda-muted">Référence</div>
 <div className="font-serif text-2xl text-nanda-ink tabular-nums">{selected.reference}</div>
 </div>
 <button onClick={() => setSelected(null)} className="text-nanda-muted hover:text-nanda-ink"><X /></button>
 </div>
 <div className="p-6 space-y-6">
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div><div className="label-overline text-nanda-muted">Statut</div><select value={selected.status} onChange={(e) => updateStatus(selected.reference, e.target.value)} className="mt-1 border border-nanda-line px-2 py-1 text-sm" data-testid="admin-quote-status-select">{STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
 <div><div className="label-overline text-nanda-muted">Catégorie</div><div className="mt-1 text-nanda-ink">{CAT_LABELS[selected.category] || selected.category}</div></div>
 <div><div className="label-overline text-nanda-muted">Contact</div><div className="mt-1 text-nanda-ink">{selected.first_name} {selected.last_name}<br />{selected.job_title || ""}<br /><a href={`mailto:${selected.email}`} className="underline">{selected.email}</a><br />{selected.phone}</div></div>
 <div><div className="label-overline text-nanda-muted">Entreprise</div><div className="mt-1 text-nanda-ink">{selected.company}<br />{selected.country}{selected.city ? ` · ${selected.city}` : ""}{selected.website ? <><br /><a href={selected.website} target="_blank" rel="noreferrer" className="underline">{selected.website}</a></> : null}</div></div>
 <div><div className="label-overline text-nanda-muted">Budget</div><div className="mt-1 text-nanda-ink">{selected.budget || "—"}</div></div>
 <div><div className="label-overline text-nanda-muted">Délai</div><div className="mt-1 text-nanda-ink">{selected.deadline}</div></div>
 </div>
 <div><div className="label-overline text-nanda-muted mb-1">Description</div><div className="text-sm text-nanda-ink whitespace-pre-wrap border border-nanda-line bg-nanda-bgAlt p-4">{selected.description}</div></div>
 {selected.details && Object.keys(selected.details).length > 0 && (
 <div><div className="label-overline text-nanda-muted mb-1">Détails projet</div><pre className="text-xs bg-nanda-bgAlt p-3 border border-nanda-line overflow-auto">{JSON.stringify(selected.details, null, 2)}</pre></div>
 )}
 {(selected.attachments || []).length > 0 && (
 <div>
 <div className="label-overline text-nanda-muted mb-2">Pièces jointes (accès sécurisé)</div>
 <ul className="space-y-2">
 {selected.attachments.map((a) => (
 <li key={a.id} className="flex items-center justify-between bg-nanda-bgAlt border border-nanda-line px-3 py-2 text-sm">
 <span className="flex items-center gap-2 truncate"><FileText size={16} className="text-nanda-terracotta shrink-0" /><span className="truncate">{a.original_name}</span><span className="text-xs text-nanda-muted ml-2">{(a.size / 1024 / 1024).toFixed(2)} MB · {a.mime}</span></span>
 <button onClick={() => downloadAttachment(selected.reference, a)} className="text-nanda-ink border border-nanda-line px-3 py-1 hover:bg-white text-xs label-overline inline-flex items-center gap-2" data-testid={`admin-quote-download-${a.id}`}><Download size={12} /> Télécharger</button>
 </li>
 ))}
 </ul>
 </div>
 )}
 <div>
 <div className="label-overline text-nanda-muted mb-2">Notes internes ({(selected.admin_notes || []).length})</div>
 {(selected.admin_notes || []).map((n, i) => (
 <div key={i} className="text-sm border-l-2 border-nanda-terracotta pl-3 py-1 mb-2 bg-nanda-bgAlt">
 <div className="text-xs text-nanda-muted">{new Date(n.at).toLocaleString("fr-FR")}</div>
 <div className="text-nanda-ink whitespace-pre-wrap">{n.text}</div>
 </div>
 ))}
 <div className="mt-3 flex gap-2">
 <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note interne…" className="flex-1 border border-nanda-line px-3 py-2 text-sm focus:outline-none focus:border-nanda-ink" data-testid="admin-quote-note-input" />
 <button onClick={addNote} disabled={!note.trim} className="bg-nanda-ink text-white px-4 py-2 label-overline text-xs disabled:opacity-50 inline-flex items-center gap-2" data-testid="admin-quote-note-add"><Send size={12} /> Ajouter</button>
 </div>
 </div>
 <div className="flex flex-wrap gap-3 pt-4 border-t border-nanda-line">
 <button onClick={() => deleteRequest(selected.reference)} className="text-xs label-overline text-nanda-terracotta border border-nanda-terracotta px-4 py-2 hover:bg-nanda-terracotta hover:text-white" data-testid="admin-quote-delete">Supprimer définitivement</button>
 <div className="ml-auto text-xs text-nanda-muted italic">IP {selected.client_ip || "—"} · {selected.locale}</div>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
